import { styled } from '@abyss/web/tools/styled';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import React, { useEffect, useRef } from 'react';

const ModalOverlay = styled('div', {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
  display: 'flex',
  pointerEvents: 'auto',

  '&[data-hidden="true"]': {
    display: 'none',
    pointerEvents: 'none',
  },
});

const ModalContent = styled('div', {
  backgroundColor: 'white',
  borderRadius: '8px',
  maxWidth: '45vw',
  maxHeight: '75vh',
  overflowY: 'auto',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  animation: 'slideIn 0.3s ease-out',
  outline: 'none',
  position: 'relative',

  '@media (max-width: 1024px)': {
    maxWidth: '75vw',
  },

  '@media (max-width: 425px)': {
    maxWidth: '90vw',
    maxHeight: '85vh',
  },

  '@keyframes slideIn': {
    from: {
      opacity: 0,
      transform: 'translateY(-20px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
});

const CloseButton = styled('button', {
  position: 'absolute',
  top: '5px',
  right: '5px',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#333',
  '&:hover': {
    opacity: 0.7,
  },
  '&:focus': {
    outline: '2px solid #0066cc',
    borderRadius: '4px',
  },
});

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  closeOnClickOutside?: boolean;
  children: React.ReactNode;
  css?: Record<string, any>;
  'data-testid'?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

export const Modal = React.forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      isOpen,
      onClose,
      closeOnClickOutside = true,
      children,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      'aria-describedby': ariaDescribedby,
      ...props
    },
    ref
  ) => {
    const overlayRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const previousActiveElement = useRef<HTMLElement | null>(null);

    // Combine refs
    React.useImperativeHandle(ref, () => contentRef.current as HTMLDivElement);

    // Focus trap and management
    useEffect(() => {
      if (!isOpen) {
        // Restore focus when modal closes
        if (
          previousActiveElement.current &&
          previousActiveElement.current.focus
        ) {
          previousActiveElement.current.focus();
        }
        return;
      }

      // Store the previously focused element
      previousActiveElement.current = document.activeElement as HTMLElement;

      // Focus the modal content
      if (contentRef.current) {
        contentRef.current.focus();
      }

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && isOpen) {
          onClose();
        }

        // Tab trap - keep focus within modal
        if (event.key === 'Tab' && contentRef.current) {
          const focusableElements = Array.from(
            contentRef.current.querySelectorAll(
              'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
            )
          ).filter((el) => {
            const element = el as HTMLElement;
            return (
              element.offsetParent !== null &&
              window.getComputedStyle(element).visibility !== 'hidden'
            );
          }) as HTMLElement[];

          if (focusableElements.length === 0) {
            event.preventDefault();
            contentRef.current.focus();
            return;
          }

          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];

          if (event.shiftKey && document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          } else if (
            !event.shiftKey &&
            document.activeElement === lastElement
          ) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      };

      if (isOpen) {
        document.addEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'hidden';
      }
      // eslint-disable-next-line consistent-return
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'unset';
        document.body.removeAttribute('aria-hidden');
      };
    }, [isOpen, onClose]);

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (closeOnClickOutside && e.target === overlayRef.current) {
        onClose();
      }
    };

    return (
      <ModalOverlay
        data-hidden={isOpen ? 'false' : 'true'}
        onClick={handleOverlayClick}
        ref={overlayRef}
        role="presentation"
        {...props}
      >
        <ModalContent
          aria-describedby={ariaDescribedby}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledby}
          aria-modal="true"
          ref={contentRef}
          role="dialog"
          tabIndex={-1}
        >
          <CloseButton aria-label="Close modal" onClick={onClose} title="Close">
            <IconSymbol icon="close" size="24px" />
          </CloseButton>
          {children}
        </ModalContent>
      </ModalOverlay>
    );
  }
);

Modal.displayName = 'Modal';
